import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getComputeTypeStreaming,
  getDefaultTime,
  tranksPlatformName,
} from '@/pages/intelligence/utils/topchartsUtils';
import useEarliestTime from '../../../utilsTmp/useEarliestTime';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import {
  getStreamTrends,
  getStreamChannelShareTable,
  getStreamChannelShareTrends,
} from '@/pages/intelligence/services/topcharts';
import StreamingTrendsCard from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Streaming/components/StreamingTrendsCard';
import ChannelsCard from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Streaming/components/ChannelsCard';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { PagingQuery } from 'tea-component';
import FacebookGaming from '@/pages/intelligence/assets/svg/dataSource/fbgaming.svg';
import Twitch from '@/pages/intelligence/assets/svg/dataSource/twitch-icon.svg';
import Youku from '@/pages/intelligence/assets/svg/dataSource/youtube.svg';
import { useIntl, getLocale, useSelector, history } from 'umi';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import useChartDownloadHooks from '@/pages/intelligence/pages/TopCharts/_hooks/useChartDownloadHooks';
import Filter from '@/pages/intelligence/pages/TopCharts/components/CommonFilter';
import { getDbiFilterParams } from '@intelligence/utils/filterUtils';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_FILTER_KEY,
  URL_SHORT_LINK_NAME,
  URL_APP_IDS,
} from '@intelligence/utils/urlPathCfg';
import { useMobileDetailShortLink } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Stats/_hook';
import { COMBINEID_REG } from '@intelligence/utils/constant';
import {
  toDateTypeFilter,
  STREAMING_METRICS_VALUE_LIST,
  toDateTypeLy,
} from '@/pages/intelligence/utils/common';
import { getFirstWeekSinceReleaseDates, isFirstWeekSinceReleaseShortcut } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utils';

export default function StreamingContext(props: any) {
  const {
    entityType = 'mobile', // TODO
    appIds = [],
    gameId,
    basicInfo = {},
    isTwoMonths, // 是否为2个月内发布的
    isBasicLoading,
    loading,
    setLoading,
    mainSegment = '',
    filterCfg,
    filterLoading,
    shortLinkValidQuery,
    shortLinkFilterValue,
    trendUrlCustomParams,
    channelUrlCustomParams,
  } = props;

  const { hasUrlShortLink, defaultValue: DEFAULT_FORM_VALUE } = filterCfg || {};
  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});
  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();

  const tableName = {
    hoursWatched: formatMessage({ id: 'dbi_metric_hourswatched' }),
    avgCCV: formatMessage({ id: 'dbi_metric_avgccv' }),
    peackCCV: formatMessage({ id: 'dbi_metric_peakccv' }),
    airtime: formatMessage({ id: 'dbi_metric_airtime' }),
  } as any;

  const getTextContent = (mainSegment: string) => {
    if (mainSegment === 'hoursWatched' || mainSegment === 'airtime') {
      return formatMessage({ id: 'dbi_info_sum_selected' });
    }
    if (mainSegment == 'avgCCV') {
      return formatMessage({ id: 'dbi_info_avg_selected' });
    }

    if (mainSegment == 'peackCCV') {
      return (
        formatMessage({ id: 'dbi_info_streaming_peakccv' }) +
        formatMessage({ id: `dbi_info_max_selected` })
      );
    }
    return '';
  };
  const getTableTitleWidth = (mainSegment: string) => {
    const tableTitleWidthConfig = {
      hoursWatched: { width: 260, width_cn: 210 },
      peakCCV: { width: 260, width_cn: 260 },
      avgCCV: { width: 260, width_cn: 260 },
      airtime: { width: 260, width_cn: 210 },
      default: { width: 260, width_cn: 210 },
    } as any;

    return tableTitleWidthConfig[mainSegment] || tableTitleWidthConfig.default;
  };

  const segOption = [
    {
      text: tableName[mainSegment],
      img: 'Twitch',
      value: 'twitch',
      dataSource: 'twitch',
      metricKey: mainSegment,
      svgIcon: Twitch,
      textContent: getTextContent(mainSegment),
      width: getTableTitleWidth(mainSegment).width,
      width_cn: getTableTitleWidth(mainSegment).width_cn,
    },
    {
      text: tableName[mainSegment],
      img: 'youtube',
      value: 'ytg',
      dataSource: 'youtube',
      metricKey: mainSegment,
      svgIcon: Youku,
      textContent: getTextContent(mainSegment),
      width: getTableTitleWidth(mainSegment).width,
      width_cn: getTableTitleWidth(mainSegment).width_cn,
    },
    {
      text: tableName[mainSegment],
      img: 'facebook',
      value: 'facebook',
      metricKey: mainSegment,
      dataSource: 'fbgaming',
      svgIcon: FacebookGaming,
      textContent: getTextContent(mainSegment),
      width: getTableTitleWidth(mainSegment).width,
      width_cn: getTableTitleWidth(mainSegment).width_cn,
    },
  ];

  const editionSegOption = [
    {
      text: tableName[mainSegment],
      value: mainSegment,
      textContent: getTextContent(mainSegment),
      width: 260,
    },
  ];
  const trendCoundTitle = {
    hoursWatched: 'Hours Watched',
    avgCCV: 'CCV',
    peackCCV: 'CCV',
    airtime: 'Airtime',
  } as any;

  // platform改变 表头的Options数量跟着改变
  const changeTrendTitleOption = (vals: string[]) => {
    if (!Array.isArray(vals) || vals?.length === 0) {
      return segOption;
    }
    const keys = segOption.map((t) => t.value);

    const newTrendSegOpt = vals.map((item) => {
      const i = keys.indexOf(item);
      return segOption[i];
    });
    return newTrendSegOpt;
  };

  // const metricsVal = segOption.map((item) => item.value)
  const metricsVal = [mainSegment];
  const gameName = basicInfo?.name;
  const [trendResData, setTrendResData] = useState<any>([]);
  const [trendLineList, setTrendLineList] = useState<any>([]);
  const [trendTableList, setTrendTableList] = useState<any>([]);
  const [editionLineList, setEditionLineList] = useState<any>([]);
  const [editionBarList, setEditionBarList] = useState<any>([]);
  const [editionTotalList, setEditionTotalList] = useState<any>([]);
  const [editionTableList, setEditionTableList] = useState<any>([]);
  const [totalNumEditon, setTotalNumEditon] = useState<number>(0);
  // const [segValTrend, setSegValTrend] = useState<string>(metricsVal[0]);
  // const [segValEdition, setSegValEdition] = useState<string>(metricsVal[0]);
  const [segValTrend, setSegValTrend] = useState<string>(mainSegment);
  const [segValEdition, setSegValEdition] = useState<string>(mainSegment);
  const [editionLoading, setEditionLoading] = useState(loading);
  const [editionLineLoading, setEditionLineLoading] = useState(loading);
  const [trendLoading, setTrendLoading] = useState(loading);
  /** 保存排序后的Top数据用于折线图请求参数 */
  const [topNameList, setTopNameList] = useState<any>({});
  const [editionTableLoading, setEditionTableLoading] = useState(false);
  //   trend标题加上数据聚合方式
  const [trendComputeType, setTrendComputeType] = useState('');
  const [trendCount, setTrendCount] = useState('');
  const defaultPlatformValues: any = ['twitch', 'ytg', 'facebook'];
  const ORDER_RATIO = '_ratio';
  const [editionChartType, setEditionChartType] = useState<string>('pie');

  // 根据ChartContext消费者，获取statsChartOption值
  const PAGEDEF = 1;
  // const PAGESIZEDEF = 5;
  const PAGESIZEDEF = 10;
  const params = {
    unified_id: gameId,
    entity_type: entityType,
    platform: defaultPlatformValues,
    metrics: metricsVal, // 固定
    page: PAGEDEF,
    page_size: PAGESIZEDEF,
    start_date: '',
    end_date: '',
    period: 'daily',
    order: 'desc', // 排序类型：倒叙为desc,正序为asc
  };
  const paramsTrend = {
    ...params,
    data_source: ['stream'],
    order_metric: 'date', // 排序字段点击哪个tab传哪个字段，"hrs_watched"，"hrs_watched_ratio", "airtime"，"airtime_ratio"，"date"
    check_metric: mainSegment, // 让后端知道是哪个指标，好做trends聚合
  };
  const paramsEdition = {
    ...params,
    data_source: ['channel'],
    check_metric: segValTrend,
    order_metric: segValTrend, // 排序字段值排序"hrs_watched"，环比排序"hrs_watched_ratio",
    ratio_type: 0,
  };
  const [trendFetchParams, setTrendFetchParams] = useState(paramsTrend);
  const [editionFetchParams, setEditionFetchParams] = useState(paramsEdition);
  const [trendError, setTrendError] = useState(false);
  const [editionError, setEditionError] = useState(false);
  const [earliestCountry] = useState('global');
  const getDataSources: () => string[] = () => ['stream'];
  const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});
  const [filterValue, setFilterVale] = useState(
    isOperateDetail ? detailFilterExtend : DEFAULT_FORM_VALUE,
  );

  // 获取可视化下载配置hooks
  const { chartDownloadConfig, setSumList, setTrendsChartDownList } = useChartDownloadHooks({
    locale,
    metricValue: mainSegment,
    entityType,
    isStreamingPage: true,
  });

  const {
    hasEarliestLogicInCurrentEntityType,
    setEarliestDay,
    earliestPeriod,
    setEarliestPeriod,
    formattedEarliestDay,
    earliestLoading,
    getEarliestDayAndEarliesPeriod,
    getBaseReleaseTimeByDateType,
    getSerieWithMarkLine,
    getShortcuttedLineSeries,
    updateEarliestDateData,
    baseReleaseTimeValue,
  } = useEarliestTime({
    entityType,
    basicInfo,
    appIds,
    dateType: trendFetchParams.period,
    topchartsState,
  });

  const firstWeekSinceReleaseTrendDates = useMemo(() => {
    if (!isFirstWeekSinceReleaseShortcut(topchartsState?.selectedTimeShortcutType)) {
      return [];
    }
    return getFirstWeekSinceReleaseDates({
      dateType: trendFetchParams.period,
      startDate: trendFetchParams.start_date,
      endDate: trendFetchParams.end_date,
      releaseDate: baseReleaseTimeValue,
    });
  }, [trendFetchParams.period, trendFetchParams.start_date, trendFetchParams.end_date, baseReleaseTimeValue, topchartsState?.selectedTimeShortcutType]);

  const firstWeekSinceReleaseEditionDates = useMemo(() => {
    if (!isFirstWeekSinceReleaseShortcut(topchartsState?.selectedTimeShortcutType)) {
      return [];
    }
    return getFirstWeekSinceReleaseDates({
      dateType: editionFetchParams.period,
      startDate: editionFetchParams.start_date,
      endDate: editionFetchParams.end_date,
      releaseDate: baseReleaseTimeValue,
    });
  }, [editionFetchParams.period, editionFetchParams.start_date, editionFetchParams.end_date, baseReleaseTimeValue, topchartsState?.selectedTimeShortcutType]);

  const {
    onTableSortChageTrend,
    onTableSortChageEdition,
    // onTableSortChageCountrie,
    updateMobileDetailShortLink,
    updatePageCfgFromShortLink,
    trendPageSortAttr,
    editionPageSortAttr,
    // countriesPageSortAttr,
  } = useMobileDetailShortLink({
    // coutriesType: countryTypeRef.current,
    trendSortMetric: 'date',
    editionSortMetric: editionFetchParams?.check_metric,
    // countrieSortMetric: chartSegment,
    shortLinkValidQuery,
    isCoutriesPage: false,
    // marketChartType,
    editionChartType,
    // setMarketChartType,
    setEditionChartType,
  });

  useEffect(() => {
    updatePageCfgFromShortLink();
  }, [shortLinkValidQuery]);

  useEffect(() => {
    if (!filterLoading && hasUrlShortLink) {
      updateMobileDetailShortLink();
    }
  }, [filterLoading, editionChartType, editionFetchParams]);

  const getMobDowDataFunc = async (
    trendParams: any,
    editionParams: any,
    updateFilterValues?: any,
  ) => {
    const newTrendFetchParams = { ...trendFetchParams, ...trendParams };
    const newEditionFetchParams = { ...editionFetchParams, ...editionParams };
    setTrendFetchParams(newTrendFetchParams);
    try {
      /** 接口速率优化：
       * 第一次时间为空时先请求trend获取时间后再请求channel；
       * 如果时间不为空，则可以同时请求2个接口
       */
      if (newTrendFetchParams.start_date === '' || newTrendFetchParams.end_date === '') {
        const { startDate, endDate, dateType } = await getTrends(newTrendFetchParams);
        if (startDate && endDate) {
          newTrendFetchParams.start_date = startDate;
          newTrendFetchParams.end_date = endDate;
          getChannelShare(
            {
              ...newEditionFetchParams,
              start_date: startDate,
              end_date: endDate,
              period: dateType,
            },
            'normal',
          );

          const newFilterValue = updateTimeFilterValue({
            // hasUrlShortLinkVal,
            // haveFilterSaveBtn: false,
            // filterSaveName,
            filterValue: updateFilterValues,
            timeFilterValues: {
              type: dateType,
              value: [startDate, endDate],
            },
          });
          // console.log('newFilterValuenewFilterValue', newFilterValue);
          // console.log('detailFilterExtend222', detailFilterExtend);
          // console.log('startDate', startDate);
          // console.log('endDate', endDate);
          // console.log('newFilterValuenewFilterValue', newFilterValue);
          // const { isOperateDetail, detailFilterExtend, setDetailFilterExtend } = useFilterExtend({});
          setDetailFilterExtend({
            ...detailFilterExtend,
            rangeTime: {
              type: toDateTypeFilter[dateType] || detailFilterExtend?.rangeTime?.type,
              value: [startDate, endDate],
            },
          });
          setFilterVale(newFilterValue);
          if (hasUrlShortLink) {
            updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue, [URL_APP_IDS]: appIds });
          }
        } else {
          const [defautStart, defautEnd] = getDefaultTime(
            newTrendFetchParams.date_type,
            isTwoMonths,
          );
          newTrendFetchParams.start_date = defautStart;
          newTrendFetchParams.end_date = defautEnd;
          setEditionLoading(false);
        }
        setTrendFetchParams(newTrendFetchParams);
      } else {
        if (hasUrlShortLink) {
          updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues, [URL_APP_IDS]: appIds });
        }
        getTrends(newTrendFetchParams);
        getChannelShare(newEditionFetchParams, 'normal');
      }
    } catch (err) {
      console.error(err);
      setTrendLoading(false);
      setEditionLoading(false);
    }
  };

  const getTrends = async (params: any) => {
    setTrendLoading(true);
    setLoading(true);
    const newFetchParams = { ...trendFetchParams, ...params };
    setTrendFetchParams(newFetchParams);
    // 添加游戏ID校验规则
    if (COMBINEID_REG.test(newFetchParams?.unified_id)) {
      try {
        const resData = await getStreamTrends(newFetchParams, entityType);
        setTrendResData(resData?.data);
        setTrendLineList(resData?.data?.metric_time_line);
        setTrendTableList(resData?.data?.data_list);
        setTrendsChartDownList(resData?.data?.data_list);

        const metricLineData = Object.values(resData?.data?.metric_time_line);
        const segValue = params?.check_metric || '';
        const sumAvgList =
          metricLineData?.map((item: any) => ({
            metric: segValue,
            value: item?.metrics?.[segValue]?.total_by_line,
            name: tranksPlatformName(item?.platform),
          })) || [];
        // 如果是需要提供可视化下载的metirc，需要调用此hooks方法，提供对应config数据
        if (Array.isArray(sumAvgList) && sumAvgList?.length > 0) {
          setSumList(sumAvgList);
        }

        const startDate = resData?.data?.start_data_time || '';
        const endDate = resData?.data?.end_data_time || '';
        newFetchParams.start_date = startDate;
        newFetchParams.end_date = endDate;
        newFetchParams.period = resData?.req?.period || 'daily';
        setTrendFetchParams(newFetchParams);
        setTrendLoading(false);
        setLoading(false);
        setTrendError(false);
        return {
          startDate,
          endDate,
          dateType: resData?.req?.period,
        };
      } catch (err) {
        setTrendLineList([]);
        setTrendTableList([]);
        setTrendLoading(false);
        setLoading(false);
        setTrendError(true);
        return {
          startDate: '',
          endDate: '',
          dateType: 'daily',
        };
      }
    } else {
      setTrendLineList([]);
      setTrendTableList([]);
      setTrendLoading(false);
      setLoading(false);
      setTrendError(true);
      return {
        startDate: '',
        endDate: '',
        dateType: 'daily',
      };
    }
  };

  const getChannelLine = async (params: any) => {
    const newFetchParams = { ...editionFetchParams, ...params };
    setEditionLineLoading(true);
    try {
      if (newFetchParams.start_date === '' || newFetchParams.end_date === '') {
        throw `Error: getting trend's time catnot be ''`;
      }
      const res = await getStreamChannelShareTrends(newFetchParams, entityType);

      setEditionLineList(res?.data?.metric_time_line || []);
      setEditionLineLoading(false);
    } catch (err) {
      console.error(err);
      setEditionLineList([]);
      setEditionLineLoading(false);
    }
  };
  const getTopData = (list: any[]) => {
    const topData: any = {};
    try {
      list?.forEach((item) => {
        if (item?.platform) {
          if (Array.isArray(topData[item?.platform])) {
            topData[item?.platform].push(item?.usrname);
          } else {
            topData[item?.platform] = [item?.usrname];
          }
        }
      });
    } catch (err) {
      console.error(err);
    }
    return topData;
  };

  const getChannelShare = async (params: any, optType: 'normal' | 'changeSegment' | 'tableOpt') => {
    const newFetchParams = { ...editionFetchParams, ...params };
    if (optType === 'normal' || optType === 'changeSegment') {
      setEditionLoading(true);
    }
    setEditionFetchParams(newFetchParams);
    try {
      const resData = await getStreamChannelShareTable(newFetchParams, entityType);
      const channelTableList = resData?.data?.channel_data_list || [];

      /** 如果是正常请求、切换标签，且为折线类型，则请求拆线数据； 如果是操作表格，则不操作 */
      if (optType === 'normal' || optType === 'changeSegment') {
        /** 储存每个标签的TOP5数据，用来请求折线图 */
        const newTopNameList = getTopData(channelTableList?.slice(0, 5));
        if (
          newFetchParams?.order === 'desc' &&
          newFetchParams?.ratio_type === 0
          // newFetchParams?.order_metric?.indexOf(ORDER_RATIO) === -1
        ) {
          // 要的是降序的top5
          setTopNameList(newTopNameList /* ?.slice(0, 5)*/);
        }
        /** 如果是降序则用新获取的，不是则用之前保存的 */
        const newTopName = newFetchParams?.order === 'desc' ? newTopNameList : topNameList;
        if (JSON.stringify(newTopName) !== '{}') {
          // 无数据时不请求折线
          getChannelLine({
            ...newFetchParams,
            channel_names: newTopName /** TOP5 */,
          });
        }
      }
      setEditionBarList(resData?.data?.pie_chart_resp || []);
      setEditionTotalList(resData?.data?.channel_data_total || []);
      setEditionTableList(channelTableList);
      setTotalNumEditon(resData?.data?.total_count);
      setEditionLoading(false);
      setEditionError(false);
    } catch (err) {
      setEditionLineList([]);
      setEditionBarList([]);
      setEditionTotalList([]);
      setEditionTableList([]);
      setTotalNumEditon(0);
      setEditionLoading(false);
      setEditionError(true);
    }
  };

  useEffect(() => {
    //   计算trend数据呼和方式
    const _trend = getComputeTypeStreaming(
      trendResData?.avg_by_line,
      trendResData?.total_by_line,
      trendResData?.max_by_line,
    );
    setTrendComputeType(_trend?.type);
    setTrendCount(_trend?.value);
  }, [JSON.stringify(trendResData)]);

  useEffect(() => {
    if (isBasicLoading || filterLoading) {
      setLoading(true);
      // setDLoading(true);
    } else {
      const hasUrlShortLinkVal = history.location?.query?.[URL_SHORT_LINK_NAME];
      const { filterValue: newFilterValues, filterParams } = getDbiFilterParams({
        // userInfo,
        urlCustomParams: trendUrlCustomParams,
        urlShortLinkFilter: shortLinkFilterValue,
        filterSaveData: null,
        filterExtend: detailFilterExtend,
        specialParams: filterCfg?.specialParams,
        isOperate: isOperateDetail,
        filterName: '',
        filterKeyList: filterCfg?.filterKeyList ?? Object.keys(DEFAULT_FORM_VALUE),
      });
      // 如果有短链接，设置为继承模式
      if (isOperateDetail || hasUrlShortLinkVal) {
        // 如果目前是继承模式，就设置继承值为过滤后的筛选器值
        setDetailFilterExtend(newFilterValues);
      }
      // console.log('newFilterValues', newFilterValues);
      // console.log('filterParams', filterParams);
      // console.log('topchartsState', topchartsState);

      const newFilterValuesTemp = newFilterValues;
      const filterParamsTemp = filterParams;
      // if (
      //   topchartsState?.topchartJumpParam?.timeType &&
      //   STREAMING_METRICS_VALUE_LIST.includes(topchartsState?.topchartJumpParam?.activeTab)
      // ) {
      //   newFilterValuesTemp = {
      //     ...newFilterValuesTemp,
      //     rangeTime: {
      //       ...newFilterValuesTemp?.rangeTime,
      //       type:
      //         toDateTypeFilter[topchartsState?.topchartJumpParam?.timeType] ||
      //         newFilterValuesTemp?.rangeTime?.type,
      //       value: ['', ''],
      //     },
      //   };
      //   filterParamsTemp = {
      //     ...filterParamsTemp,
      //     date_type:
      //       toDateTypeLy[topchartsState?.topchartJumpParam?.timeType] ||
      //       filterParamsTemp?.date_type,
      //     start_date: '',
      //     end_date: '',
      //   };
      // }
      // console.log('newFilterValuesTemp', newFilterValuesTemp);
      // console.log('filterParamsTemp', filterParamsTemp);
      setFilterVale(newFilterValuesTemp);
      const newtrendFetchParams = {
        ...trendFetchParams,
        ...(trendUrlCustomParams || {}),
        ...filterCfg?.handleFilterToApiParams(filterParamsTemp),
      };
      const neweditionFetchParams = {
        ...editionFetchParams,
        ...(channelUrlCustomParams || {}),
        ...filterCfg?.handleFilterToApiParams(filterParamsTemp),
      };

      getMobDowDataFunc(newtrendFetchParams, neweditionFetchParams, newFilterValuesTemp);
      // dispatch({ type: 'topchartsState/setFrom', payload: '' });

      if (hasEarliestLogicInCurrentEntityType) {
        updateEarliestDateData({
          dataSources: getDataSources(),
          metrics: [mainSegment],
          regionType: 'region',
          regions: ['global'],
          dateType: newtrendFetchParams.period,
        });
      }
    }
  }, [isBasicLoading, filterLoading]);

  const handleConfirm = async (value: any, filter, myFilterValue) => {
    const newtrendFetchParams = { ...trendFetchParams, ...value };
    const neweditionFetchParams = { ...editionFetchParams, ...value };
    setTrendFetchParams(newtrendFetchParams);
    getMobDowDataFunc(newtrendFetchParams, neweditionFetchParams, filter);
    //   /**时间筛选器类型切换时， */
    const { earliestDay, earliestPeriod } = getEarliestDayAndEarliesPeriod(
      value?.period,
      mainSegment,
    );
    setEarliestDay(earliestDay);
    setEarliestPeriod(earliestPeriod);
  };

  const onEditionTableSortChage = async (e: SortByItemType) => {
    onTableSortChageEdition(e);
    const newFetchParams = {
      ...editionFetchParams,
      order: e?.sortVal,
      check_metric: e?.sortMetric,
      order_metric: e?.ratioType === 1 ? e?.sortMetric + ORDER_RATIO : e?.sortMetric,
      ratio_type: e?.ratioType,
      page: 1,
      page_size: 5,
    };
    setEditionTableLoading(true);
    await getChannelShare(newFetchParams, 'tableOpt');
    setEditionTableLoading(false);
  };

  const onEditionTablePagingChange = async (e: PagingQuery) => {
    const newFetchParams = {
      ...editionFetchParams,
      page: e?.pageIndex || 1,
      page_size: e?.pageSize || 5,
    };
    setEditionTableLoading(true);
    await getChannelShare(newFetchParams, 'tableOpt');
    setEditionTableLoading(false);
  };

  const changeEditionChartType = (value: string) => {
    setEditionChartType(value);
  };
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 20,
          marginBottom: 10,
        }}
      >
        <Filter
          defaultValue={DEFAULT_FORM_VALUE}
          filterCfg={filterCfg}
          // timeFilterValues={{
          //   type: trendFetchParams.period,
          //   value: [trendFetchParams.start_date, trendFetchParams.end_date],
          // }}
          filterConfirm={handleConfirm}
          filterValue={filterValue}
          setFilterVale={setFilterVale}
          sinceReleaseTime={baseReleaseTimeValue}
          earliestDataTime={earliestPeriod}
        />
        <ExportTable
          entityType="mobile"
          businessType="mobile-stats-countries"
          isDisableDownload={loading}
          isShowMaxDownloadRowsTips={false}
          immediateDownloadCharts={true}
          // chartDownloadConfig={{
          //   title: `${tableName[mainSegment]}${locale === 'zh-CN' ? '' : ' '}${formatMessage({ id: `dbi_detail_trend` })}`
          // }}
          chartDownloadConfig={chartDownloadConfig}
          tableParams={trendFetchParams}
        />
      </div>
      <StreamingTrendsCard
        // 请求状态
        loading={trendLoading}
        // 折线图数据
        lineData={trendTableList === null ? [] : trendLineList}
        // 表格数据，
        tableData={trendTableList}
        // 用于表头指标渲染
        tableHeaderOptions={changeTrendTitleOption(trendFetchParams.platform)}
        chartCardTitle={formatMessage({ id: `dbi_detail_trend` })}
        segVal={segValTrend}
        // segOption={segOption}
        // handleChangeSegment={handleChangeSegmentTrend}
        entityType={entityType}
        gameName={gameName}
        gameCover={basicInfo?.cover}
        dateType={trendFetchParams.period}
        startDate={trendFetchParams?.start_date}
        endDate={trendFetchParams?.end_date}
        trendFetchParams={trendFetchParams}
        hasEarliestLogicInCurrentEntityType={hasEarliestLogicInCurrentEntityType}
        showReleaseDate={hasEarliestLogicInCurrentEntityType}
        showEarliestDate={hasEarliestLogicInCurrentEntityType}
        baseReleaseTime={getBaseReleaseTimeByDateType({
          dateType: trendFetchParams.period,
          baseReleaseMonth: topchartsState.baseReleaseMonth,
          baseReleaseWeek: topchartsState.baseReleaseWeek,
          baseReleaseTime: topchartsState.baseReleaseTime,
        })}
        earliestCountry={earliestCountry}
        earlistDate={formattedEarliestDay}
        earliestPeriod={earliestPeriod}
        getSerieWithMarkLine={getSerieWithMarkLine}
        getShortcuttedLineSeries={getShortcuttedLineSeries}
        trendComputeType={trendComputeType}
        trendCount={trendCount}
        segementTitle={trendCoundTitle[mainSegment]}
        rightTitle={tableName[mainSegment]}
        cardStatus={{
          isLoading: loading || trendLoading,
          isError: trendError,
          isNoData: !trendTableList?.length,
        }}
        earliestLoading={earliestLoading}
        ifLineChartLegendOn={true}
        onTableSortChage={onTableSortChageTrend}
        pageSortAttr={trendPageSortAttr}
        firstWeekSinceReleaseDates={firstWeekSinceReleaseTrendDates}
      />

      <ChannelsCard
        entityType={entityType}
        // Loading状态, 如果当前为折线图，则等折线和表格同时加载完。
        // 折线图数据
        lineData={editionLineList}
        // 柱状图数据
        barOrPieData={editionBarList}
        totalData={editionTotalList}
        // 表格数据，
        tableData={editionTableList}
        // 标签切换组
        segVal={segValEdition}
        tableHeaderOptions={editionSegOption}
        // segOption={segOption}
        // handleChangeSegment={handleChangeSegmentEdition}
        // 卡片标题放进去即可
        chartCardTitle={formatMessage({ id: `dbi_detail_channels_share` })}
        gameName={gameName}
        gameCover={basicInfo?.cover}
        // 点击排序、分页返回相应字段
        apiData={editionFetchParams}
        totalNum={totalNumEditon}
        // showEchartIcon={false}
        dateType={editionFetchParams.period}
        segementTitle={trendCoundTitle[mainSegment]}
        chartsTitle={gameName}
        cardStatus={{
          isLoading: loading || editionLoading,
          isError: editionError,
          isNoData: !editionTableList?.length,
          tableLoading: editionTableLoading,
        }}
        onTableSortChage={onEditionTableSortChage}
        onTablePagingChange={onEditionTablePagingChange}
        pageSortAttr={{
          tablePageIndex: editionFetchParams?.page,
          tablePageSize: editionFetchParams?.page_size,
          currentSortValue: editionFetchParams?.order,
          currentSortMetric: editionFetchParams?.check_metric,
          currentRatioType: editionFetchParams?.ratio_type,
        }}
        startDate={editionFetchParams?.start_date}
        endDate={editionFetchParams?.end_date}
        chartType={editionChartType}
        setChartType={changeEditionChartType}
        firstWeekSinceReleaseDates={firstWeekSinceReleaseEditionDates}
      />
    </>
  );
}
